import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { from, switchMap, catchError, throwError } from 'rxjs';
import { AuthService } from './auth.service';
import { ToastService } from './toast.service';
import { environment } from '../../environments/environment';

/**
 * Forwards auth headers to the Spring Boot API on every API request.
 * Local mode: the headers from assets/local-auth.json. Live mode: the Firebase ID token
 * as a Bearer header. Non-API requests (assets, external search) pass through untouched.
 *
 * Also enforces server-side session validity: a 401 from the API (in live/firebase mode)
 * means the token is invalid/revoked — e.g. the account was deleted or the session revoked
 * on another device — so we force a fresh sign-in.
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  if (!req.url.startsWith(environment.apiBaseUrl)) {
    return next(req);
  }
  const auth = inject(AuthService);
  const router = inject(Router);
  const toast = inject(ToastService);
  return from(auth.getApiHeaders()).pipe(
    switchMap(headers => {
      const authReq = headers && Object.keys(headers).length
        ? req.clone({ setHeaders: headers })
        : req;
      return next(authReq).pipe(
        catchError((err: HttpErrorResponse) => {
          if (err.status === 401 && !auth.isLocalBypass()) {
            // Server rejected the token → drop local state and re-login.
            void auth.handleInvalidSession();
          } else if (err.status === 403 && err.error?.code === 'account_deleted') {
            // Account was soft-deleted (possibly on another device) → show the restore screen.
            if (!router.url.includes('account-deleted')) {
              void auth.refreshUserConfig().then(() => router.navigate(['/v2/account-deleted']));
            }
          } else if (err.status === 402) {
            // Plan token allotment exhausted (EntitlementService) → prompt to upgrade.
            // Toast, not alert(): a native alert steals focus and blocks the JS thread.
            toast.show(
              "You've reached your plan's token limit. Upgrade your membership in Settings → Membership to continue.",
              'warning',
              8000
            );
          }
          return throwError(() => err);
        })
      );
    })
  );
};
